<template>
  <div class="grid">
    <table class="table">
      <thead v-if="form.value.headers">
        <tr>
          <th v-if="dynamic"></th>
          <th v-for="(header, index) in form.value.headers" :key="index">
            <component
              :is="header.type"
              :key="index"
              :block="header"
              :blocks="blocksData"
              :definitions="definitions"
              :asset="asset"
            />
          </th>
        </tr>
      </thead>
      <tbody>
        <Row
          v-for="(row, index) in form.value.rows"
          :key="index"
          :index="index"
          :row="row"
          :blocks="blocksData"
          :definitions="definitions"
          :dynamic="dynamic"
          :asset="asset"
          :showActions="showActions"
          v-on:destroy="deleteRow"
        />
      </tbody>
    </table>


    <div
      v-if="dynamic && showActions"
      class="row mt-4"
    >
      <div class="col flex-grow-1">
        <LoadingButton
          v-if="showInsert && showActions"
          @click.native="addRow"
          class="btn btn-primary"
          :loading="adding"
        >
          {{ block.settings.label }}
        </LoadingButton>
        <span v-if="showInsert && block.settings.helper" class="d-block mb-4 text-muted">
          <small>{{ block.settings.helper }}</small>
        </span>
      </div>

      <div class="col flex-grow-0">
        <div class="d-flex flex-nowrap">
          <Tag
            v-if="showActions && !editingData"
            @click.native="editingData = true"
            :block="blockData"
            :withCounterparty="state.withCounterparty"
          />
          <CommentsTrigger
            v-if="showActions && !editingData"
            v-on:toggle="comments = !comments"
            :showing="comments"
            :comments="commentsData"
          />
        </div>
      </div>
    </div>

    <Actions
      v-if="showActions && editingData"

      v-on:updated="updated"
      v-on:cancel="cancel"

      :block="blockData"

      :deletable="false"
      :parentComponent="true"

      :asset-id="asset.id"
      :model="asset.model"
    />

    <Comments
      v-if="comments"
      :comments="commentsData"
      :contract="block.contract_id"
      :block="block.id"
      v-on:commentsUpdated="commentsUpdated"
      v-on:toggle="comments = !comments"
    />


  </div>
</template>

<script>
import helpers from '@/Mixins/helpers';
import contractHelpers from '@/Mixins/contractHelpers';
import contractComponents from '@/Mixins/contractComponents';
import LoadingButton from '@/Shared/LoadingButton';
import Row from './Row';

import Actions from '@/Pages/Asset/Contract/Actions/Actions';
import Comments from '@/Pages/Asset/Contract/Comments/Comments';
import CommentsTrigger from '@/Pages/Asset/Contract/Comments/CommentsTrigger';
import Tag from '@/Pages/Asset/Contract/Actions/Tag';

export default {
  mixins: [helpers, contractHelpers, contractComponents],

  components: {
    LoadingButton,
    Row,
    Actions,
    Comments,
    CommentsTrigger,
    Tag,
  },

  props: {
    dynamic: {
      type: Boolean,
      default: false,
    },
    block: Object,
    blocks: Array,
    definitions: Array,
    showActions: Boolean,
    asset: Object,
  },

  data() {
    return {
      blockData: this.block,
      blocksData: this.blocks,
      commentsData: this.block.comments,

      editingData: this.editing,
      adding: false,
      editing: false,
      versions: false,
      comments: false,

      errors: null,
      saving: false,

      form: {
        value: this.block.value,
      },

      newBlockLength: this.block.template ? this.block.template.length : null,
      newRow: [],
    };
  },

  watch: {
    block(block) {
      this.form.value = block.value;
    },
    editing(value) {
      this.editingData = value;
    },
  },

  computed: {
    state() {
      return this.$store.state.assetState;
    },

    showInsert() {
      if (!this.block.settings || !this.block.settings.expandable) {
        return false;
      }

      // If it's with the counter party then it needs to be reviewing
      if (this.state.withCounterparty) {
        return this.state.state === 'reviewing';
      }

      if (this.state.state === 'pending') {
        return this.userHasRole('approver');
      }

      return ['incomplete', 'editing', 'approved', 'signed'].includes(this.state.state);
    },
  },

  methods: {
    createUuid() {
      // eslint-disable-next-line no-bitwise, no-mixed-operators
      return ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, (c) => (c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> c / 4).toString(16));
    },

    addRow() {
      const { template } = this.block;
      this.adding = true;

      template.forEach((block) => {
        // If there's a contract_reference key which is null, then we'll be creating a dynamic block
        if ('contract_reference' in block) {
          // Find the latest row number used
          // const nextRowNumber = this.blockData.value.rows.length + 1;

          const newBlock = block;
          const reference = this.createUuid();

          // Save the new blocks
          newBlock.contract_id = this.block.contract_id;
          newBlock.contract_reference = reference;
          this.saveDynamicBlock(newBlock);

          this.newRow.push({
            type: 'Dynamic',
            reference,
          });
        } else {
          this.newRow.push(block);
        }
      });
    },

    saveDynamicBlock(block) {
      window.axios
        .post('/contract-block', block)
        .then((response) => {
          this.blocksData.push(response.data);
          this.newBlockLength -= 1;

          if (this.newBlockLength === 0) {
            this.form.value.rows.push(this.newRow);
            this.save();
          }
        })
        .catch((error) => {
          this.errors = error.response.data.errors;
          throw new Error(error);
        });
    },

    dynamicBlock(reference) {
      const block = this.blocksData.find(
        (b) => b.contract_reference === reference,
      );

      if (!block) {
        throw new Error("Can't find Dynamic Block for reference: ", reference);
      }

      return block;
    },

    deleteRow(index) {
      // loop through the row and delete any dynamic blocks
      this.form.value.rows[index].forEach((block) => {
        if (block.type === 'Dynamic') {
          const dynamicBlock = this.dynamicBlock(block.reference);
          this.$nextTick(() => {
            window.axios
              .delete(`/contract-block/${dynamicBlock.id}`)
              .then(() => {
                this.saving = false;
                this.editing = false;
              })
              .catch((error) => {
                this.saving = false;
                this.errors = error.response.data.errors;
                throw new Error(error);
              });
          });
        }
      });

      // Now delete the row
      this.form.value.rows.splice(index, 1);

      this.$nextTick(() => {
        // Save the new grid structure
        this.save();
      });
    },

    save() {
      this.$nextTick(() => {
        window.axios
          .put(`/contract-block/${this.block.id}`, this.form)
          .then(({ data }) => {
            this.adding = false;
            this.fetchAssetState(this.asset.id);
            this.blockData = data;
            this.editingData = this.userHasRole('approver');
            this.newRow = [];
            this.newBlockLength = this.block.template.length;
          });
      });
    },

    updated(block) {
      this.blockData = block;
    },

    cancel() {
      this.editingData = false;
    },

    commentsUpdated(comments) {
      this.commentsData = comments;
    },
  },

};
</script>
